import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import { NotificationAlert } from "src/components/common";
import { DatePickerFormInputFabric, SelectFormInput } from "src/components/form";
import { renderRegistrationFormSaveBlock } from "src/helpers/alert";
import { InstitutionCreatePersonnelBreadcrumbs, InstitutionPersonnelBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { RegistrationComponent, renderFormControls } from "src/helpers/formHelper";
import { findLookup, generateOptionsFromLookup } from "src/helpers/lookupHelper";
import useNavigate from "src/hooks/useNavigate";
import { RegistrationFormComponent } from "src/interfaces/forms/IRegistrationComponent";
import { IRegistrationFormState } from "src/reducers/reactReduxForms";
import { ITrialPersonnelReduxFormState } from "src/reducers/reactReduxForms/trialPersonnel";
import * as InstitutionActions from "../../../actions/institution";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialPersonnelActions from "../../../actions/trialPersonnel";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { createPersonnelSearchLoader } from "../../../functions/personnel";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { InstitutionPersonnelSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import "./InstitutionPersonnelPage.scss";

interface InstitutionPersonnelPageProps {
    form: Dtos.TrialPersonnel & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: ITrialPersonnelReduxFormState;
    trialPersonnel: Dtos.TrialPersonnel;
    institution: Dtos.Institution;
    lookups: Dtos.Lookup[];
    loadingTrialPersonnel: boolean;
    loadTrialPersonnelSuccess: boolean;
    loadTrialPersonnelFailure: boolean;
    loadTrialPersonnelErrorCode: string;
    savingTrialPersonnel: boolean;
    saveTrialPersonnelSuccess: boolean;
    saveTrialPersonnelFailure: boolean;
    creatingTrialPersonnel: boolean;
    createTrialPersonnelSuccess: boolean;
    createTrialPersonnelFailure: boolean;
    validatingTrialPersonnel: boolean;
    loadingInstitution: boolean;
    loadInstitutionSuccess: boolean;
    loadInstitutionFailure: boolean;
    loadInstitutionErrorCode: string;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialPersonnel: TrialPersonnelActions.ITrialPersonnelClearActionCreator;
    loadTrialPersonnel: TrialPersonnelActions.ITrialPersonnelLoadByIdActionCreator;
    saveTrialPersonnel: TrialPersonnelActions.ITrialPersonnelSaveActionCreator;
    createTrialPersonnel: TrialPersonnelActions.ITrialPersonnelCreateActionCreator;
    validateTrialPersonnel: TrialPersonnelActions.ITrialPersonnelValidateActionCreator;
    clearInstitution: InstitutionActions.IInstitutionClearActionCreator;
    loadInstitution: InstitutionActions.IInstitutionLoadByIdActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.trialPersonnel";

const InstitutionPersonnelPage: FC<InstitutionPersonnelPageProps> = ({
    form,
    formState,
    formProperties,
    trialPersonnel,
    institution,
    lookups,
    loadingTrialPersonnel,
    loadTrialPersonnelFailure,
    loadTrialPersonnelErrorCode,
    savingTrialPersonnel,
    saveTrialPersonnelSuccess,
    saveTrialPersonnelFailure,
    creatingTrialPersonnel,
    validatingTrialPersonnel,
    loadingInstitution,
    loadInstitutionFailure,
    loadInstitutionErrorCode,
    validationFailures,
    permissions,
    clearTrialPersonnel,
    loadTrialPersonnel,
    saveTrialPersonnel,
    createTrialPersonnel,
    validateTrialPersonnel,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems,
    loadInstitution,
    clearInstitution
}) => {
    //
    // -- Params --
    const { institutionId } = useParams<{ institutionId: string }>();
    const { trialPersonnelId } = useParams<{ trialPersonnelId: string }>();
    const institutionIdNumber = parseInt(institutionId, 10);
    const trialPersonnelIdNumber = parseInt(trialPersonnelId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();


    // Load Trial Personnel
    useEffect(() => {
        if (institutionIdNumber && trialPersonnelIdNumber && !trialPersonnel) {
            setDrawerItems(
                InstitutionPersonnelSubMenu(
                    institutionIdNumber,
                    trialPersonnelIdNumber,
                    permissions
                )
            );
            setBreadcrumbs(
                InstitutionPersonnelBreadcrumbs(
                    institutionIdNumber,
                    trialPersonnelIdNumber
                )
            );

            loadTrialPersonnel(trialPersonnelIdNumber);
            loadInstitution(institutionIdNumber);
        } else if (
            institutionIdNumber &&
            !trialPersonnelIdNumber &&
            !trialPersonnel
        ) {
            setBreadcrumbs(
                InstitutionCreatePersonnelBreadcrumbs(institutionIdNumber)
            );
            setDrawerItems([]);
            createTrialPersonnel(null, institutionIdNumber, null);
            loadInstitution(institutionIdNumber);
        } else if (trialPersonnel && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        institutionIdNumber,
        trialPersonnelIdNumber,
        trialPersonnel,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        InstitutionPersonnelSubMenu,
        InstitutionPersonnelBreadcrumbs,
        InstitutionCreatePersonnelBreadcrumbs,
        createTrialPersonnel,
        setFormInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionFailure) {
            navigate(`/error/${loadInstitutionErrorCode}`);
        }
        if (loadTrialPersonnelFailure) {
            navigate(`/error/${loadTrialPersonnelErrorCode}`);
        }
    }, [
        loadInstitutionFailure,
        loadTrialPersonnelFailure,
        loadInstitutionErrorCode,
        loadTrialPersonnelErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            institutionIdNumber &&
            trialPersonnelIdNumber &&
            institution &&
            trialPersonnel
        ) {
            setBreadcrumbs(
                InstitutionPersonnelBreadcrumbs(
                    institutionIdNumber,
                    trialPersonnelIdNumber,
                    trialPersonnel?.institution?.name,
                    trialPersonnel?.personnel?.displayWithSalutation
                )
            );
        } else if (
            institutionIdNumber &&
            !trialPersonnelIdNumber &&
            institution &&
            !trialPersonnel
        ) {
            setBreadcrumbs(
                InstitutionCreatePersonnelBreadcrumbs(
                    institutionIdNumber,
                    institution?.name
                )
            );
        }
    }, [
        institutionIdNumber,
        trialPersonnelIdNumber,
        institution,
        trialPersonnel,
        setBreadcrumbs,
        InstitutionPersonnelBreadcrumbs,
        InstitutionCreatePersonnelBreadcrumbs,
    ]);

    // Reload trial personnel after saving
    useEffect(() => {
        if (!savingTrialPersonnel && saveTrialPersonnelSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Institution personnel saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                navigate(`/institutions/${institutionIdNumber}/personnel`);
            } else if (!trialPersonnelIdNumber) {
                navigate(
                    `/institutions/${institutionIdNumber}/personnel/${trialPersonnel?.id}/details`
                );
                clearTrialPersonnel();
                clearInstitution();
            }
        }
    }, [
        savingTrialPersonnel,
        saveTrialPersonnelSuccess,
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingTrialPersonnel && saveTrialPersonnelFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingTrialPersonnel, saveTrialPersonnelFailure]);

    // Cleanup effect to clear the personnel on component unmount
    useEffect(() => {
        return () => {
            clearTrialPersonnel();
            clearInstitution();
            resetForm(reduxFormName);
        };
    }, [clearTrialPersonnel, clearInstitution, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialPersonnel = {
                trialId: null,
                groupLevelTypeId: 1,
            } as Dtos.TrialPersonnel;

            if (!data.institutionId) {
                changes.institutionId = institutionIdNumber;
            }

            await saveTrialPersonnel(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialPersonnel) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        navigate(`/institutions/${institutionIdNumber}/personnel`);
    };

    const loadReduxForm = () => {
        let extraData: Dtos.TrialPersonnel = {} as Dtos.TrialPersonnel;

        loadForm(
            reduxFormName,
            Object.assign({ ...trialPersonnel }, extraData)
        );
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialPersonnel = {} as Dtos.TrialPersonnel;

            if (form && hasBeenSubmitted && !validatingTrialPersonnel) {
                await validateTrialPersonnel(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const personnelSearchLoader = useMemo(() => {
        return createPersonnelSearchLoader(
            form?.personnel 
                ? { label: form?.personnel?.displayWithSalutation, value: form?.personnelId } as Dtos.PersonnelOption 
                : null
        );
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                undefined,
                trialPersonnelIdNumber,
                permissions,
                Dtos.Permission.InstitutionPersonnelUpdate,
                Dtos.Permission.InstitutionPersonnelCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialPersonnel ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            personnelDetails: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Personnel Details</h1>
                        <div className="card card-shadow">
                            <div className="form-row py-4 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialPersonnel",
                                        {
                                            personnelId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                size: 2,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    value: form?.personnelId ?? null,
                                                    cacheOptions: true,
                                                    defaultOptions: form?.personnel
                                                        ? [{ label: form.personnel.displayWithSalutation, value: form.personnel.id }]
                                                        : true,
                                                    placeholder: "Type to search for personnel...",
                                                    clearable: true, 
                                                    loadOptions: personnelSearchLoader,
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-lg-1",
                                                fieldClassName: "col-sm-12 col-lg-11 generic-form-field mr-0 pr-0",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            roleDetails: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Role Details</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialPersonnel",
                                        {
                                            roleId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                size: 2,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "trialPersonnel",
                                                                "roleId",
                                                                lookups
                                                            ),
                                                            form.roleId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-lg-1",
                                                fieldClassName:
                                                    "col-sm-12 col-lg-11 generic-form-field mr-0 pr-0",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trialPersonnel",
                                        {
                                            dateStarted: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-2",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trialPersonnel",
                                        {
                                            dateCeased: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.personnelId,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-2",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "trialPersonnel",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        trialPersonnelIdNumber,
        permissions,
        savingTrialPersonnel,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        personnelSearchLoader
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.InstitutionPersonnelView}
            loading={
                loadingTrialPersonnel ||
                savingTrialPersonnel ||
                creatingTrialPersonnel ||
                loadingInstitution
            }
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<InstitutionPersonnelPageProps> => {
    let trialPersonnel: Dtos.TrialPersonnel | undefined = !(
        state.trialPersonnel.formData instanceof Array
    )
        ? state.trialPersonnel.formData
        : undefined;
    let institution: Dtos.Institution | undefined = !(
        state.institutions.formData instanceof Array
    )
        ? state.institutions.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialPersonnel: trialPersonnel!,
        institution: institution!,
        loadingTrialPersonnel:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Pending,
        loadTrialPersonnelSuccess:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Success,
        loadTrialPersonnelFailure:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Failure,
        loadTrialPersonnelErrorCode: state.trialPersonnel.loadState.errorCode,
        savingTrialPersonnel:
            state.trialPersonnel.saveState &&
            state.trialPersonnel.saveState.status == RequestState.Pending,
        saveTrialPersonnelSuccess:
            state.trialPersonnel.saveState &&
            state.trialPersonnel.saveState.status == RequestState.Success,
        saveTrialPersonnelFailure:
            state.trialPersonnel.saveState &&
            state.trialPersonnel.saveState.status == RequestState.Failure,
        creatingTrialPersonnel:
            state.trialPersonnel.createState &&
            state.trialPersonnel.createState.status == RequestState.Pending,
        createTrialPersonnelSuccess:
            state.trialPersonnel.createState &&
            state.trialPersonnel.createState.status == RequestState.Success,
        createTrialPersonnelFailure:
            state.trialPersonnel.createState &&
            state.trialPersonnel.createState.status == RequestState.Failure,
        validatingTrialPersonnel:
            state.trialPersonnel.validateState &&
            state.trialPersonnel.validateState.status == RequestState.Pending,
        loadingInstitution:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Pending,
        loadInstitutionSuccess:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Success,
        loadInstitutionFailure:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Failure,
        loadInstitutionErrorCode: state.institutions.loadState.errorCode,
        user: state.user.data,
        permissions: permissions,
        lookups: state.trialPersonnel.lookups,
        form: state.reduxForms.trialPersonnel,
        formState: state.trialPersonnel.formState,
        formProperties: state.trialPersonnel.formProperties,
        reduxFormState: state.reduxForms.formState.trialPersonnel,
        validationFailures: trialPersonnel
            ? state.trialPersonnel.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<InstitutionPersonnelPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.LoadTrialPersonnelById,
            dispatch
        ),
        clearTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.Clear,
            dispatch
        ),
        saveTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.SaveTrialPersonnel,
            dispatch
        ),
        createTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.CreateTrialPersonnel,
            dispatch
        ),
        validateTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.ValidateTrialPersonnel,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        loadInstitution: bindActionCreators(
            InstitutionActions.LoadInstitutionById,
            dispatch
        ),
        clearInstitution: bindActionCreators(
            InstitutionActions.Clear,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(InstitutionPersonnelPage);
